/**
 * Canonical vocabulary for the eISF catalog's `required_fields_json`
 * column — sourced from the 88-row catalog seed. Shared between the
 * eISF Master Data catalog form (declares which fields a doc type
 * requires) and the binder upload card (Training module Phase 3 —
 * renders + validates those fields at upload time) so both stay in
 * sync on the same value/label pairs.
 */
export const REQUIRED_FIELD_OPTIONS = [
  { value: 'advertisement_type', label: 'Advertisement type' },
  { value: 'ae_grading', label: 'Adverse-event grading' },
  { value: 'brief_title', label: 'Brief title' },
  { value: 'document_date', label: 'Document date' },
  { value: 'lab_certification_type', label: 'Lab certification type' },
  { value: 'language', label: 'Language' },
  { value: 'manual_type', label: 'Manual type' },
  { value: 'monitoring_visit_type', label: 'Monitoring-visit type' },
  { value: 'organization', label: 'Organization' },
  { value: 'participant', label: 'Participant' },
  { value: 'person', label: 'Person' },
  { value: 'product', label: 'Product' },
  { value: 'response_type', label: 'Response type' },
  { value: 'study', label: 'Study' },
  { value: 'submission_type', label: 'Submission type' },
  { value: 'training_type', label: 'Training type' },
] as const;

export function labelForRequiredField(value: string): string {
  return REQUIRED_FIELD_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/**
 * Bug found live (UAT: catalog required fields all rendered as plain text
 * boxes regardless of what they actually are). Per-field input type —
 * `study` is never rendered at upload (it is bound from the binder and
 * stamped server-side), so it is not listed here.
 */
export type RequiredFieldInputType = 'text' | 'date' | 'dropdown' | 'participant' | 'person';

export const REQUIRED_FIELD_TYPES: Record<string, RequiredFieldInputType> = {
  document_date: 'date',
  language: 'dropdown',
  advertisement_type: 'dropdown',
  ae_grading: 'dropdown',
  lab_certification_type: 'dropdown',
  manual_type: 'dropdown',
  monitoring_visit_type: 'dropdown',
  response_type: 'dropdown',
  submission_type: 'dropdown',
  training_type: 'dropdown',
  participant: 'participant',
  person: 'person',
  brief_title: 'text',
  organization: 'text',
  product: 'text',
};

export function requiredFieldType(key: string): RequiredFieldInputType {
  return REQUIRED_FIELD_TYPES[key] ?? 'text';
}

/**
 * Dropdown vocabularies for the bounded-choice fields. Placeholder lists
 * pending final client sign-off on exact wording — easy to edit here,
 * nowhere else references the literal option strings.
 */
export const REQUIRED_FIELD_DROPDOWN_OPTIONS: Record<string, string[]> = {
  language: ['English', 'Spanish', 'French', 'German', 'Mandarin', 'Other'],
  advertisement_type: ['Newspaper', 'Radio', 'Television', 'Social Media', 'Flyer/Poster', 'Website', 'Other'],
  ae_grading: [
    'Grade 1 — Mild',
    'Grade 2 — Moderate',
    'Grade 3 — Severe',
    'Grade 4 — Life-threatening',
    'Grade 5 — Death',
  ],
  lab_certification_type: ['CLIA', 'CAP', 'ISO 15189', 'Other'],
  manual_type: ['Pharmacy Manual', 'Laboratory Manual', 'Imaging Manual', 'Manual of Procedures (MOP)', 'Other'],
  monitoring_visit_type: [
    'Site Initiation Visit (SIV)',
    'Interim Monitoring Visit (IMV)',
    'Close-Out Visit (COV)',
    'Remote Visit',
  ],
  response_type: ['Deficiency Response', 'IRB Query Response', 'Sponsor Query Response', 'Other'],
  submission_type: ['Initial Submission', 'Amendment', 'Continuing Review', 'Safety Report', 'Final Report'],
  training_type: ['Protocol Training', 'GCP Training', 'Site Staff Training', 'Sponsor Training', 'Other'],
};
